/**
 * MobileActionBar — the fixed bottom action bar shown on the booking detail view
 * at mobile widths (the right-rail Actions card takes over on lg+; the page
 * mounts exactly one of the two via useViewport). Rebook + retire, gated to
 * CONFIRMED (disabled-CTA pattern); the retire button is additionally gated on
 * `refund:bookings` (`canRetire`), same treatment as the desktop rail card.
 * Rebook opens `RebookBookingDrawer` (BON-146); retire opens the refund drawer
 * (BON-147/BON-335) or, on a comp, the cancel dialog (BON-337) — one button,
 * because the two are mutually exclusive. The cutoff override is NOT desktop-only: a
 * same-day move is exactly what gets asked for on a phone, so the same gate and
 * the same explicit in-drawer opt-in apply at every breakpoint. Resend
 * confirmation (BON-630) is the third button, gated the same way as its
 * desktop counterpart in `BookingActions`.
 *
 * `mobileActionButton` is a plain (lowercase) function, not a component, same
 * reasoning as `BookingActions`' `actionRow`: it is called three times from
 * one place, not reused across files, and folding the shared markup + tone
 * logic into it is what keeps this component's own complexity under the
 * lint cap.
 */
import { CalendarClock, Mail, RotateCcw, XCircle } from 'lucide-react';

import type { BookingActionsProps } from './BookingActions';
import {
  rebookAffordance,
  resendConfirmationAffordance,
  retireAffordance,
} from './bookingDetailModel';
import { useGiftPassesEnabled } from './use-gift-passes-enabled';

/** The same type as the desktop card, not a copy: a field added to one and
 *  forgotten on the other splits behaviour between breakpoints. */
export type MobileActionBarProps = BookingActionsProps;

const base =
  'flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition';

function mobileActionButton({
  icon,
  label,
  ariaLabel,
  enabled,
  hint,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  /** Visible text — kept short so three buttons fit the bar. */
  label: string;
  /** Accessible name — the full, unambiguous action name (see the desktop
   *  card, which uses the same wording for its title/aria-label). */
  ariaLabel: string;
  enabled: boolean;
  hint: string;
  onClick: () => void;
  /** `filled` for the primary action (Rebook); `outline` for the other two —
   *  matches the desktop card's visual hierarchy. */
  tone: 'filled' | 'outline';
}) {
  const toneCls =
    tone === 'filled'
      ? enabled
        ? 'bg-moss-600 text-white'
        : 'cursor-not-allowed bg-stone-100 text-stone-400 dark:bg-stone-700/50'
      : enabled
        ? 'border border-moss-400 text-moss-700 dark:text-moss-300'
        : 'cursor-not-allowed border border-stone-200 text-stone-400 dark:border-stone-700';

  return (
    <button
      type="button"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      aria-label={ariaLabel}
      title={enabled ? undefined : hint}
      className={`${base} ${toneCls}`}
    >
      {icon}
      {label}
    </button>
  );
}

export function MobileActionBar({
  status,
  canRebook,
  canOverrideCutoff,
  cutoffPassed,
  canRetire,
  isComp,
  amountPaidCents,
  workshopEndsAt,
  canResendConfirmation,
  onRebook,
  onRetire,
  onResendConfirmation,
}: MobileActionBarProps) {
  const giftPassesEnabled = useGiftPassesEnabled();
  const rebook = rebookAffordance(status, canRebook, cutoffPassed, canOverrideCutoff);
  const retire = retireAffordance(
    status,
    isComp,
    amountPaidCents,
    workshopEndsAt,
    canRetire,
    giftPassesEnabled,
  );
  const resend = resendConfirmationAffordance(status, canResendConfirmation);
  const retireAriaLabel = retire.kind === 'cancel' ? 'Cancel booking' : 'Refund';
  // Three buttons now share this bar (BON-630) — "Cancel booking" is the only
  // visible label long enough to crowd the row, so it shortens to "Cancel"
  // while the accessible name keeps the unambiguous full wording.
  const retireVisibleLabel = retire.kind === 'cancel' ? 'Cancel' : 'Refund';

  return (
    <div className="fixed bottom-0 inset-x-0 z-20 flex gap-3 border-t border-stone-200 bg-white px-4 py-3 dark:border-stone-700 dark:bg-[#1e2521]">
      {mobileActionButton({
        icon: <CalendarClock size={18} />,
        label: 'Rebook',
        ariaLabel: 'Rebook',
        enabled: rebook.enabled,
        hint: rebook.hint,
        onClick: onRebook,
        tone: 'filled',
      })}
      {mobileActionButton({
        icon: retire.kind === 'cancel' ? <XCircle size={18} /> : <RotateCcw size={18} />,
        label: retireVisibleLabel,
        ariaLabel: retireAriaLabel,
        enabled: retire.enabled,
        hint: retire.hint,
        onClick: onRetire,
        tone: 'outline',
      })}
      {mobileActionButton({
        icon: <Mail size={18} />,
        label: 'Resend',
        ariaLabel: 'Resend confirmation',
        enabled: resend.enabled,
        hint: resend.hint,
        onClick: onResendConfirmation,
        tone: 'outline',
      })}
    </div>
  );
}
